/**
 * The Clinical Pathway tab: how the engine read this patient. First the phenotypes and the patient's answer on
 * each symptom item, then every pathway the patient's sex allows, in the engine's ranking order, with each part of
 * its Treatment Relevance in plain words (symptoms and phenotype, labs and data, route, prior response), and its
 * locks, caps, review and caution notes and missing data.
 *
 * Bands and words only. The engine's points, its component values and the phenotype fit numbers stay in the API
 * (lib/hrt/view.ts); the words here come from the pathway's own findings and the developer spec's tables.
 */

import type { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { InfoHint } from './ui';
import { EmptyCard, LockGlyph, SaveNote, StepCard } from './HrtUi';
import {
  BAND_BADGE,
  BAND_WORD,
  HRT_PROGRAM_KEY,
  LAB_RULE,
  ROUTE_WORDS,
  SYMPTOM_NAME,
  errorText,
  hrtKey,
  priorWord,
  ruleSource,
  shortDay,
  unlockWords,
  type HrtFinding,
  type HrtPathway,
  type HrtPatientView,
  type HrtPhenotype,
} from '../lib/hrt';

/** What made the engine run again (HrtEvaluation.trigger). */
const TRIGGER_WORD: Record<string, string> = {
  intake: 'the intake',
  monthly: 'a Monthly check-in',
  lab: 'a lab result',
  vitals: 'a blood pressure reading',
  confirmation: 'a provider confirmation',
  view: 'a review of the chart',
};

const SEVERITY_TONE: Record<string, string> = {
  None: 'bg-paper-deep text-ink-muted',
  Mild: 'bg-info-soft text-navy',
  Moderate: 'bg-info-soft text-navy',
  Severe: 'bg-plum-soft text-plum-ink font-semibold',
  'Very severe': 'bg-plum-soft text-plum-ink font-semibold',
};

export function HrtPathwayTab({ view }: { view: HrtPatientView }) {
  const queryClient = useQueryClient();
  const rerun = useMutation({
    mutationFn: () => api.post<{ created: boolean; evaluationId: string | null }>(`/hrt/patients/${view.patient.id}/evaluate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hrtKey(view.patient.id) });
      void queryClient.invalidateQueries({ queryKey: HRT_PROGRAM_KEY });
    },
  });
  const ev = view.evaluation;
  if (!ev) {
    return <EmptyCard title="No hormone evaluation yet" body="The Clinical Pathway appears once the intake has the hormone questions." />;
  }

  return (
    <div className="space-y-2.5">
      <StepCard
        title="Clinical Pathway"
        subtitle={`How MCOS read ${view.patient.firstName}'s intake, labs and confirmations: the phenotypes the answers point to, then every pathway with what set its band.`}
        action={
          <div className="flex items-center gap-2">
            <InfoHint label="About Treatment Relevance">{ev.notice}</InfoHint>
            <button
              type="button"
              onClick={() => rerun.mutate()}
              disabled={rerun.isPending}
              className="inline-flex h-[30px] items-center rounded-md border border-hairline bg-white px-3 text-[12.5px] text-navy hover:bg-mint disabled:opacity-50"
              data-testid="rerun-evaluation"
            >
              {rerun.isPending ? 'Running' : 'Run again'}
            </button>
          </div>
        }
        testId="hrt-pathway-tab"
      >
        <p className="text-[12.5px] text-ink-muted">
          Evaluated {shortDay(ev.createdAt)} after {TRIGGER_WORD[ev.trigger] ?? 'a change'} · {ev.rulesVersion}
        </p>
        {rerun.isSuccess && (
          <SaveNote tone="ok">{rerun.data.created ? 'Evaluated again with the latest data.' : 'Nothing has changed since the last evaluation.'}</SaveNote>
        )}
        {rerun.isError && <SaveNote tone="error">{errorText(rerun.error)}</SaveNote>}
      </StepCard>

      {ev.track === 'HRT-EXCEPTION' && ev.exception && (
        <p className="rounded-[10px] border border-hairline bg-white px-4 py-3 text-[13.5px] text-navy">{ev.exception}</p>
      )}

      {ev.phenotypes.length > 0 && (
        <StepCard title="Phenotypes" subtitle="The patient's own answers on each symptom item, None to Very severe. A phenotype is active when the answers point to it.">
          <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">
            {ev.phenotypes.map((phenotype) => (
              <PhenotypeCard key={phenotype.id} phenotype={phenotype} />
            ))}
          </div>
        </StepCard>
      )}

      <StepCard title="Pathways" subtitle="In the engine's ranking order. Each part of the band in words; locked pathways last.">
        {ev.pathways.length === 0 ? (
          <p className="text-[13.5px] text-ink-muted">No pathway for this track.</p>
        ) : (
          <>
            <div className="hidden grid-cols-[minmax(0,1.5fr)_128px_minmax(0,1.1fr)_minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,0.8fr)] gap-3 border-b border-hairline px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted lg:grid">
              <span>Pathway</span>
              <span>Band</span>
              <span>Symptoms and phenotype</span>
              <span>Labs and data</span>
              <span>Route</span>
              <span>Prior response</span>
            </div>
            <ul className="divide-y divide-hairline" data-testid="pathway-table">
              {ev.pathways.map((pathway) => (
                <PathwayRow key={pathway.id} view={view} pathway={pathway} />
              ))}
            </ul>
          </>
        )}
      </StepCard>
    </div>
  );
}

function PhenotypeCard({ phenotype }: { phenotype: HrtPhenotype }) {
  return (
    <div className="rounded-lg border border-hairline" data-testid={`phenotype-${phenotype.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-3 py-2">
        <p className="flex items-center gap-1.5 font-display text-[15.5px] text-navy" style={{ fontWeight: 560 }}>
          {phenotype.name}
          <span className="font-sans text-[12px] font-normal text-ink-muted">{phenotype.id}</span>
          {phenotype.id === 'F-MIX' && (
            <InfoHint label="How the mixed phenotype reads">
              Active when the androgen / sexual symptoms and the vasomotor or the GSM symptoms are both present. Each line shows the worst main
              symptom in that group.
            </InfoHint>
          )}
        </p>
        <span className="flex items-center gap-2">
          {phenotype.leadsOn.length > 0 && <span className="text-[12px] text-ink-muted">Leads on {phenotype.leadsOn.join(', ')}</span>}
          <span
            className={`rounded-[5px] px-1.5 py-[3px] text-[11px] font-semibold leading-none ${phenotype.pointsTo ? 'bg-mint text-forest' : 'bg-paper-deep text-ink-muted'}`}
            data-testid={`phenotype-state-${phenotype.id}`}
          >
            {phenotype.pointsTo ? 'Active' : 'Not active'}
          </span>
        </span>
      </div>
      <ul className="divide-y divide-hairline">
        {phenotype.items.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-[13px]">
            <span className="min-w-0">
              <span className="text-navy">{item.name || SYMPTOM_NAME[item.id] || item.id}</span>
              <span className="ml-1.5 text-[11.5px] text-ink-muted">{item.role === 'primary' ? 'Primary' : item.role === 'cluster' ? 'Symptom group' : 'Supporting'}</span>
            </span>
            <span className={`shrink-0 rounded-[5px] px-1.5 py-[3px] text-[11.5px] leading-none ${item.severityLabel ? SEVERITY_TONE[item.severityLabel] : 'text-ink-muted'}`}>
              {item.severityLabel ?? (item.role === 'cluster' ? 'None' : 'Not asked')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The symptom part of the band, in words. */
function symptomWords(pathway: HrtPathway, phenotypes: HrtPhenotype[]): string {
  if (pathway.caps.some((cap) => cap.id === 'NO-TRIGGER')) return 'No compatible symptoms';
  const dominant = pathway.dominantPhenotype;
  if (!dominant) return 'No compatible symptoms';
  const pointsTo = phenotypes.find((p) => p.id === dominant.id)?.pointsTo;
  return `${dominant.name ?? dominant.id}${pointsTo === false ? ', not active' : ', compatible symptoms'}`;
}

/** The lab and data part (developer spec tab "Lab Gates"), from the pathway's own lock and missing list. */
function labWords(pathway: HrtPathway): string {
  const rule = LAB_RULE[pathway.id];
  const lock = pathway.locks.find((l) => l.id === 'LAB-MALE-T' || l.id === 'LAB-FEMALE-T');
  const missing = pathway.missing;
  if (rule === 'male_t') {
    if (lock) return 'Not met: two low early-morning fasting total testosterone results needed';
    return missing.length > 0 ? `Gate met; still missing ${missing.join(', ')}` : 'Gate met, required data on file';
  }
  if (rule === 'female_t') {
    if (lock) return 'Baseline total testosterone and SHBG needed';
    const rest = missing.filter((m) => m !== 'total testosterone' && m !== 'SHBG');
    return rest.length > 0 ? `Total testosterone and SHBG on file; still missing ${rest.join(', ')}` : 'Total testosterone and SHBG on file';
  }
  return missing.length > 0 ? `Missing: ${missing.join(', ')}` : 'Every required answer on file';
}

function PathwayRow({ view, pathway }: { view: HrtPatientView; pathway: HrtPathway }) {
  const phenotypes = view.evaluation?.phenotypes ?? [];
  const route = ROUTE_WORDS[pathway.id];
  const cells: Array<{ label: string; value: string; note?: string | null }> = [
    { label: 'Symptoms and phenotype', value: symptomWords(pathway, phenotypes) },
    { label: 'Labs and data', value: labWords(pathway) },
    { label: 'Route', value: route?.route ?? 'Not listed', note: route?.note ?? null },
    { label: 'Prior response', value: priorWord(view.confirmations, pathway.id) },
  ];
  const hasNotes =
    pathway.locks.length + pathway.caps.length + pathway.review.length + pathway.caution.length + pathway.flags.length > 0;
  return (
    <li className="px-3 py-3" data-testid={`pathway-row-${pathway.id}`}>
      <div className="grid grid-cols-1 gap-x-3 gap-y-2 lg:grid-cols-[minmax(0,1.5fr)_128px_minmax(0,1.1fr)_minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,0.8fr)]">
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold leading-snug text-navy">
            <span className="mr-1.5 text-[12px] font-normal text-ink-muted">{pathway.rank}.</span>
            {pathway.name}
          </p>
          <p className="text-[12px] text-ink-muted">
            {pathway.id}
            {pathway.controlled ? ' · Controlled' : ''}
          </p>
        </div>
        <div>
          <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-[5px] px-2 py-[4px] text-[12px] font-semibold leading-none ${BAND_BADGE[pathway.band]}`}>
            {pathway.band === 'LOCKED' && <LockGlyph size={11} />}
            {BAND_WORD[pathway.band]}
          </span>
          {pathway.reviewRequired && <span className="ml-1 rounded-[5px] bg-risk-yellow-soft px-1.5 py-[4px] text-[11px] font-semibold leading-none text-risk-yellow">Review</span>}
        </div>
        {cells.map((cell) => (
          <div key={cell.label} className="min-w-0 text-[12.5px] leading-snug text-ink-muted">
            <span className="block text-[10.5px] font-semibold uppercase tracking-[0.06em] text-ink-muted lg:hidden">{cell.label}</span>
            {cell.value}
            {cell.note && <span className="block text-[11.5px] text-ink-muted">{cell.note}</span>}
          </div>
        ))}
      </div>
      {hasNotes && (
        <ul className="mt-2 space-y-1 text-[12.5px] leading-snug lg:pl-[18px]">
          {pathway.locks.map((lock) => {
            const how = unlockWords(lock, view.confirmations);
            return (
              <Note key={`lock-${lock.id}`} tone="text-alert" word="Locked" finding={lock}>
                {lock.text}
                {how ? <span className="text-ink-muted"> Unlocks when: {how.text.charAt(0).toLowerCase() + how.text.slice(1)}.</span> : null}
              </Note>
            );
          })}
          {pathway.caps.map((cap) => (
            <Note key={`cap-${cap.id}`} tone="text-ink-muted" word="Band capped" finding={{ id: cap.id, name: cap.name, cite: cap.cite }}>
              {cap.text}
            </Note>
          ))}
          {pathway.review.map((finding) => (
            <Note key={`review-${finding.id}`} tone="text-risk-yellow" word="Review" finding={finding}>
              {finding.text}
              {finding.referralText ? ` Referral: ${finding.referralText}.` : ''}
            </Note>
          ))}
          {pathway.caution.map((finding) => (
            <Note key={`caution-${finding.id}`} tone="text-ink-muted" word="Caution" finding={finding}>
              {finding.text}
            </Note>
          ))}
          {pathway.flags.map((flag) => (
            <li key={`flag-${flag}`} className="text-navy">
              <b className="font-semibold">Note</b>: {flag}
            </li>
          ))}
        </ul>
      )}
      {pathway.missing.length > 0 && pathway.band !== 'LOCKED' && (
        <p className="mt-1.5 text-[12.5px] text-ink-muted lg:pl-[18px]">
          <b className="font-semibold">Missing data</b>: {pathway.missing.join(', ')}
        </p>
      )}
    </li>
  );
}

/** One note under a pathway: the kind in bold, the clinician words, and the rule id and source behind the hint. */
function Note({ tone, word, finding, children }: { tone: string; word: string; finding: Pick<HrtFinding, 'id' | 'name' | 'cite'> & { source?: string | null }; children: ReactNode }) {
  return (
    <li className={`flex items-start gap-2 ${tone}`}>
      <span className="min-w-0 flex-1">
        <b className="font-semibold">{word}</b>: {children}
      </span>
      <span className="shrink-0 pt-[1px]">
        <InfoHint label={`Source of ${finding.name}`}>{ruleSource(finding)}</InfoHint>
      </span>
    </li>
  );
}
